import type { FirebaseApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import type { TakiConfig } from '@taki/runtime-config';
import { CHAMPS_D_IDENTITE, FAMILLES, FUSEAU_PAR_DEFAUT, champsNatifs, messageFamilleInconnue, type ChampNatif } from '@taki/data-model';
import { initFirebase } from './firebase.js';
import { createLecteurEtatDuChatter } from './conversations-chatter.js';
import { type ContactsRepository, createContactsRepository } from './contacts.js';
import { type AssetsRepository, createAssetsRepository } from './assets.js';
import { type LoanRepository, createLoanRepository } from './loans.js';
import { type WarrantyRepository, createWarrantyRepository } from './warranties.js';
import { type IncidentRepository, createIncidentRepository } from './incidents.js';
import { type MeasurementRepository, createMeasurementRepository } from './measurements.js';
import { type FormRepository, createFormRepository } from './forms.js';
import { type CampaignRepository, createCampaignRepository } from './campaigns.js';
import { type AccessGrantRepository, createAccessGrantRepository } from './accessGrants.js';
import { type PolicyRepository, createPolicyRepository } from './policies.js';
import { type RegisterSessionRepository, createRegisterSessionRepository } from './registerSessions.js';
import { type RelationshipRepository, createRelationshipRepository } from './relationships.js';
import { type GroupRepository, createGroupRepository } from './groups.js';
import { type PlaceRepository, createPlaceRepository } from './places.js';
import { type PriceListRepository, createPriceListRepository } from './priceLists.js';
import { type StockRepository, createStockRepository } from './stocks.js';
import { type StockMovementRepository, createStockMovementRepository } from './stockMovements.js';
import { type AvailabilityRepository, createAvailabilityRepository } from './availabilities.js';
import { type ShiftRepository, createShiftRepository } from './shifts.js';
import { type AbsenceRepository, createAbsenceRepository } from './absences.js';
import { type AttendanceRepository, createAttendanceRepository } from './attendances.js';
import { type SubscriptionRepository, createSubscriptionRepository } from './subscriptions.js';
import { type AgreementRepository, createAgreementRepository } from './agreements.js';
import { type DonationRepository, createDonationRepository } from './donations.js';
import { type ExpenseRepository, createExpenseRepository } from './expenses.js';
import { type ShipmentRepository, createShipmentRepository } from './shipments.js';
import { type CustodyRepository, createCustodyRepository } from './custodies.js';
import { type TicketRepository, createTicketRepository } from './tickets.js';
import { type InspectionRepository, createInspectionRepository } from './inspections.js';
import { type MessageRepository, createMessageRepository } from './messages.js';
import { type ConversationRepository, createConversationRepository } from './conversations.js';
import { creerMaPersonne } from './acces-par-fiche.js';
import { type ReviewRepository, createReviewRepository } from './reviews.js';
import { type ConsentRepository, createConsentRepository } from './consents.js';
import { type VoucherRepository, createVoucherRepository } from './vouchers.js';
import { type CreditRepository, createCreditRepository } from './credits.js';
import { type CompositionRepository, createCompositionRepository } from './compositions.js';
import { type LotRepository, createLotRepository } from './lots.js';
import { type RouteRepository, createRouteRepository } from './routes.js';
import { type WorkerRepository, createWorkerRepository } from './workers.js';
import { type ProductRepository, createProductRepository } from './products.js';
import { type ResourceRepository, createResourceRepository } from './resources.js';
import { type BookingRepository, createBookingRepository } from './bookings.js';
import { type EventRepository, createEventRepository } from './events.js';
import { type RegistrationRepository, createRegistrationRepository } from './registrations.js';
import { type OrderRepository, createOrderRepository } from './orders.js';
import { type InvoiceRepository, createInvoiceRepository } from './invoices.js';
import { type PaymentRepository, createPaymentRepository } from './payments.js';
import { type WorkOrderRepository, createWorkOrderRepository } from './workOrders.js';
import { type TaskRepository, createTaskRepository } from './tasks.js';
import { type TodoRepository, createTodoRepository } from './todos.js';
import { type TimesheetRepository, createTimesheetRepository } from './timesheets.js';
import { type DocumentRepository, createDocumentRepository } from './documents.js';
import { type ContentRepository, createContentRepository } from './contents.js';
import { type FilesRepository, createFilesRepository } from './files.js';
import { type ProjectRepository, createProjectRepository } from './projects.js';
import { type ListingRepository, createListingRepository } from './listings.js';
import { type OpportunityRepository, createOpportunityRepository } from './opportunities.js';
import { type CareRecordRepository, createCareRecordRepository } from './careRecords.js';
import { type CredentialRepository, createCredentialRepository } from './credentials.js';
import { type MemberRepository, createMemberRepository } from './members.js';
import { type PersonneRepository, createPersonneRepository } from './personnes.js';
import { type JournalClient, createJournalClient } from './journal.js';
import { type BrandingRepository, createBrandingRepository } from './branding.js';
import { type SequencesRepository, createSequencesRepository } from './sequences.js';
import {
  type AppsInstalleesRepository,
  type EnvironnementRepository,
  createAppsInstalleesRepository,
  createEnvironnementRepository,
} from './installation-apps.js';
import {
  type AppsInstancieesRepository,
  createAppsInstancieesRepository,
} from './apps-instanciees.js';
import {
  type TemplateRepository,
  type TemplateTargetRepository,
  createTemplateRepository,
  createTemplatePlanValidator,
  sansValeursVides,
} from './templates.js';
import { type RecurrenceRepository, createRecurrenceRepository } from './recurrences.js';
import { type FluxClient, createFluxClient } from './flux.js';
import { type ConnexionsClient, createConnexionsClient } from './connexions.js';
import { type ComptesClient, createComptesClient } from './acces-comptes.js';
import { type MarketDataClient, createMarketDataClient } from './market-data.js';
import { type IaSettingsRepository, createIaSettingsRepository } from './ia-settings.js';
import { type WhatsappClient, createWhatsappClient } from './whatsapp.js';
import { type VuesHtmlClient, createVuesHtmlClient } from './vues-html.js';
import { type NotesClient, createNotesClient } from './notes.js';
import { createNotificationsRepository, type NotificationsRepository } from './notifications.js';
import { type IaHistoryRepository, createIaHistoryRepository } from './ia-history.js';
import { type LiaConversationsRepository, createLiaConversationsRepository } from './lia-conversations.js';
import type { CountOptions, ListOptions } from './list-query.js';
import { creerSignalDesEcritures, envelopperLesEcritures, type SignalDesEcritures } from './ecritures.js';

/**
 * LES VERBES D'UNE FAMILLE, vus SANS savoir laquelle.
 *
 * Un ecran qui suit un LIEN ENTRE PORTES ("les interventions de CE materiel")
 * ne connait sa famille qu'a l'execution : elle est une CHAINE, celle que la
 * porte declare (`porte.jeu.family`, le meme mot que dans `taki.fields.json`).
 * Il lui faut donc lire une famille designee par son nom, sans que le socle ait
 * a connaitre les 55 types concrets. C'est ce que cette interface promet, et
 * rien de plus.
 *
 * QUATRE LECTURES ET UN SEUL VERBE D'ECRITURE, `update`. Ni `create` (c'est
 * `createIn`, plus bas, qui nettoie l'entree avant d'ecrire), ni `archive`, ni
 * `remove` : le geste qui a demande `update` est « Lier une fiche existante »,
 * et c'est le SENS d'un lien qui l'impose. La reference vit dans le document de
 * celui qui DESIGNE, jamais dans celui qui est designe : rattacher, c'est
 * patcher UNE clef sur la fiche choisie, et ca ne peut s'ecrire nulle part
 * ailleurs. Tant que le type ne le promettait pas, l'hote passait par un cast
 * verifie a la main, c'est-a-dire par une promesse ecrite deux fois.
 *
 * `patch` EST UN `Record<string, unknown>`, comme l'entree de `createIn` et
 * pour la meme raison : le type CONCRET du patch (`AssetUpdateInput`) n'existe
 * pas quand la famille est une chaine. Le repository valide par Zod avant toute
 * ecriture, lui, et pose les champs standard.
 *
 * ET ELLE NE REND RIEN. Le document mis a jour n'a pas davantage de type
 * concret ici, et le promettre ferait croire qu'on peut s'en contenter : un
 * ecran qui vient d'ecrire RECHARGE sa liste et son compte au serveur (c'est
 * deja la regle de `LiensPort.rattacher`, cote `@taki/ui`).
 *
 * `list` rend des `Record<string, unknown>` et non le type de la famille : le
 * type CONCRET (`Asset`, `CareRecord`) n'existe pas quand la famille est une
 * chaine. C'est exactement ce que les vues du socle consomment deja
 * (`packages/ui/src/lib/templates.tsx` projette une fiche de la meme facon) :
 * la validation Zod a bien eu lieu, elle, dans le repository.
 */
export interface FamilyRepository {
  list(options?: ListOptions): Promise<Array<Record<string, unknown>>>;
  count(options?: CountOptions): Promise<number>;
  sum(field: string, options?: CountOptions): Promise<number>;
  average(field: string, options?: CountOptions): Promise<number | null>;
  update(id: string, patch: Record<string, unknown>): Promise<void>;
}

/**
 * LE REPOSITORY COMPLET D'UNE FAMILLE DESIGNEE PAR SON NOM : ce que
 * `repositoryOf` promet, PLUS les verbes qu'un ecran a besoin de jouer sur une
 * fiche (`get`, `create`, `archive`, `unarchive`, `remove`) et la liste des
 * champs natifs de la famille, lue dans `@taki/data-model` (`champsNatifs`).
 *
 * POURQUOI UNE SECONDE SURFACE. `repositoryOf` est ne pour LIRE une famille
 * qu'on ne connait qu'a l'execution (cartes liees, totaux) et patcher une fiche
 * ; un ecran qui INTERPRETE une declaration (`app-ecran-generique`) doit aussi
 * la creer, l'archiver, la supprimer, exactement comme `sdk.contacts` le fait
 * pour un ecran ecrit en TypeScript. Toutes les familles ont ces verbes ; ce
 * repository les promet, et REFUSE avant d'ecrire un patch dont une clef de
 * racine n'est ni un champ natif de la famille ni `customFields` : le schema
 * `partial()` de la famille l'aurait retiree en silence.
 *
 * La fabrique est `creerFamilleRepository`, plus bas.
 */
export interface FamilleRepository extends FamilyRepository {
  /** Le nom de la famille, au pluriel, tel qu'il a ete demande. */
  readonly famille: string;
  get(id: string): Promise<Record<string, unknown> | null>;
  create(input: Record<string, unknown>): Promise<{ id: string }>;
  archive(id: string): Promise<void>;
  unarchive(id: string): Promise<void>;
  remove(id: string): Promise<void>;
  /**
   * SUIVRE UNE FICHE EN DIRECT (`observe-ops.ts`) : le meme converter que
   * `get`, et `local` vrai pour l'echo d'une ecriture de CE client. Toutes les
   * familles le portent (`createArchiveOps`) ; ce repository le relaie tel quel.
   */
  observe(
    id: string,
    onChange: (record: Record<string, unknown> | null, meta: { local: boolean }) => void,
    onError?: (error: unknown) => void,
  ): () => void;
  /** Les champs natifs de la famille (`champsNatifs`, `@taki/data-model`). */
  champs(): readonly ChampNatif[];
}

/**
 * CREER DANS UNE FAMILLE DESIGNEE PAR SON NOM, le verbe d'ecriture que
 * `repositoryOf` refusait de donner.
 *
 * POURQUOI IL EXISTE MAINTENANT. « + Ajouter » n'ouvre plus une fenetre ecrite
 * par l'app : il ouvre une FICHE VIDE que le socle projette par le binding
 * ORDINAIRE de la porte, et c'est le socle qui la cree au clic sur « Creer »
 * (decision du fondateur, 09/09/2026). Or le socle ne connait la famille qu'a
 * l'execution, comme une chaine (`porte.jeu.family`). Sans ce verbe, chaque app
 * devrait rappeler `sdk.assets.create(...)` a la main, c'est-a-dire ecrire la
 * ligne que le chantier supprime. C'est la MEME regle que l'instanciation d'un
 * modele (`templates.instantiate`), qui cree deja dans une famille nommee par
 * une chaine : meme resolution, meme nettoyage, meme surface minimale.
 *
 * ON REFUSE AVANT D'ECRIRE. Une famille inconnue leve, elle ne rend pas
 * `undefined` : sinon la faute n'apparaitrait qu'a l'appel suivant, loin de sa
 * cause. Et le vocabulaire de l'erreur est celui de `repositoryOf`, au PLURIEL,
 * parce que c'est le meme nom qui se lit dans `taki.fields.json` et dans le
 * `jeu.family` d'une porte.
 *
 * LES CHAINES VIDES DE PREMIER NIVEAU PARTENT (`sansValeursVides`), pour la
 * raison qui a motive cette fonction cote modeles : une fiche vide qu'on ne
 * remplit qu'a moitie garde une clef par case touchee, et une case videe vaut
 * `''`, que `z.string().min(1).optional()` refuse partout. On ne descend pas
 * dans `customFields` : ce sac appartient a l'app, son schema accepte le vide.
 *
 * FONCTION PURE DE FIRESTORE, exportee expres : elle refuse, elle nettoie, elle
 * ecrit, et ces trois-la se prouvent avec un objet de trois lignes qui sait
 * `create`, sans emulateur ni projet Firebase.
 */
export async function creerDansFamille(
  resolveRepository: (family: string) => TemplateTargetRepository | null,
  family: string,
  input: Record<string, unknown>,
): Promise<{ id: string }> {
  const repository = resolveRepository(family);
  // Le vocabulaire du refus vit dans `@taki/data-model` (`messageFamilleInconnue`),
  // ecrit une seule fois pour les trois portes d'entree par le nom.
  if (repository == null) throw new Error(messageFamilleInconnue(family));
  const { id } = await repository.create(sansValeursVides(input));
  return { id };
}

/**
 * LES VERBES QU'UN `FamilleRepository` PROMET. La verification porte sur ce qui
 * sera appele : une famille qui n'aurait pas l'un d'eux casserait sur « is not
 * a function » au moment du clic, loin de sa cause.
 */
const VERBES_DE_FAMILLE = [
  'list',
  'count',
  'sum',
  'average',
  'update',
  'get',
  'create',
  'archive',
  'unarchive',
  'remove',
  'observe',
] as const;

/**
 * La surface REELLE d'un repository de famille, vue sans son type concret :
 * ce que les onze verbes ci-dessus acceptent et rendent. `archive`, `unarchive`,
 * `remove` et `update` rendent l'entite ; `FamilleRepository` ne la promet pas
 * (cf. son commentaire), c'est pourquoi ils sont declares `unknown` ici.
 */
interface SurfaceDeFamille {
  list(options?: ListOptions): Promise<Array<Record<string, unknown>>>;
  count(options?: CountOptions): Promise<number>;
  sum(field: string, options?: CountOptions): Promise<number>;
  average(field: string, options?: CountOptions): Promise<number | null>;
  update(id: string, patch: Record<string, unknown>): Promise<unknown>;
  get(id: string): Promise<Record<string, unknown> | null>;
  archive(id: string): Promise<unknown>;
  unarchive(id: string): Promise<unknown>;
  remove(id: string): Promise<unknown>;
  observe(
    id: string,
    onChange: (record: Record<string, unknown> | null, meta: { local: boolean }) => void,
    onError?: (error: unknown) => void,
  ): () => void;
}

/**
 * LE REPOSITORY COMPLET D'UNE FAMILLE DESIGNEE PAR SON NOM (cf.
 * `FamilleRepository`), fabrique a part de `createTakiSdk` pour la meme raison
 * que `creerDansFamille` : elle ne connait qu'un objet qui sait lire et ecrire,
 * donc elle se prouve avec un faux repository de trois lignes, sans emulateur
 * ni projet Firebase.
 *
 * `resolveEntry` est la table des familles, c'est-a-dire l'objet SDK lui-meme
 * (`familyEntry`) : une seule liste de familles dans le SDK, jamais deux.
 *
 * DEUX REFUS, TOUS LES DEUX AVANT LA PREMIERE LECTURE : un nom qui n'est pas une
 * famille, et une entree qui n'a pas les onze verbes (`sdk.members`,
 * `sdk.branding`, `sdk.db`).
 */
export function creerFamilleRepository(
  resolveEntry: (famille: string) => Record<string, unknown> | null,
  nom: string,
): FamilleRepository {
  const entree = resolveEntry(nom);
  if (entree == null) throw new Error(messageFamilleInconnue(nom));
  const manquants = VERBES_DE_FAMILLE.filter((verbe) => typeof entree[verbe] !== 'function');
  if (manquants.length > 0) {
    throw new Error(
      `La famille "${nom}" existe dans le SDK mais ne porte pas ${manquants.join(', ')} : ` +
        "elle n'a pas la surface commune aux familles. C'est le cas de l'annuaire des membres, " +
        'qui vit dans une sous-collection (`orgs/{orgId}/members`), sans cadrage `orgId` ni ' +
        'agrégats, de l\'annuaire des personnes, et du branding, qui est un document de réglage : ' +
        'passez par `sdk.members`, `sdk.personnes` ou `sdk.branding`.',
    );
  }
  const repository = entree as unknown as SurfaceDeFamille;
  const cible = entree as unknown as TemplateTargetRepository;

  /**
   * UN PATCH NE PORTE QUE DES CHAMPS NATIFS ET LE SAC. `XUpdateInputSchema` est
   * un `.partial()` NON strict : une clef inconnue serait RETIREE en silence,
   * l'ecriture reussirait et l'ecran croirait avoir ecrit. On refuse donc avant
   * d'ecrire, avec la clef et la famille dans le message, comme le faisait
   * chaque app a la main (`natifInconnu`, apps/hello/src/apps/contact/views.ts).
   * La couleur et la photo de reference sont des champs du SOCLE, sur toute
   * famille (`CHAMPS_D_IDENTITE`), et non des natifs : elles passent aussi.
   */
  const verifierPatch = (patch: Record<string, unknown>): void => {
    const natifs = new Set([...champsNatifs(nom).map((champ) => champ.key), ...CHAMPS_D_IDENTITE]);
    for (const clef of Object.keys(patch)) {
      if (clef === 'customFields' || natifs.has(clef)) continue;
      throw new Error(
        `Le champ « ${clef} » n'est pas un champ de la famille « ${nom} » : rien n'a été écrit.`,
      );
    }
  };

  return {
    famille: nom,
    list: (options) => repository.list(options),
    count: (options) => repository.count(options),
    sum: (field, options) => repository.sum(field, options),
    average: (field, options) => repository.average(field, options),
    get: (id) => repository.get(id),
    async update(id, patch) {
      verifierPatch(patch);
      await repository.update(id, patch);
    },
    // La creation passe par `creerDansFamille` : meme nettoyage des chaines
    // vides que `sdk.createIn`, donc une seule regle pour les deux chemins.
    create: (input) => creerDansFamille(() => cible, nom, input),
    // Les trois etats rendent l'entite cote repository ; ici ils ne rendent
    // RIEN, pour la raison qui vaut deja pour `update` : un ecran qui vient
    // d'ecrire RECHARGE sa liste et son compte au serveur.
    async archive(id) {
      await repository.archive(id);
    },
    async unarchive(id) {
      await repository.unarchive(id);
    },
    async remove(id) {
      await repository.remove(id);
    },
    // La lecture en direct passe telle quelle : meme converter que `get`.
    observe: (id, onChange, onError) => repository.observe(id, onChange, onError),
    champs: () => champsNatifs(nom),
  };
}

/**
 * Instance du SDK Taki, point d'accès UNIQUE aux données pour une app.
 * Aucune app ne doit importer firebase/firestore directement (cage IA).
 */
export interface TakiSdk {
  /**
   * Le fuseau de l'org : son reglage (`settings/environnement`, suivi des que
   * l'hote observe l'environnement), sinon `timezone` de la config, sinon
   * Europe/Luxembourg. `docs/socle/fuseau-org.md`.
   */
  readonly timezone?: string;
  app: FirebaseApp;
  db: Firestore;
  /**
   * Le relais de données de marché (cours, splits, résolution d'un ISIN).
   *
   * Ce n'est PAS un repository : rien ici ne vit dans une collection Firestore
   * que l'app pourrait lire. C'est un service SERVEUR, appelé par callable,
   * parce que sa clé d'API ne peut pas descendre dans un navigateur. Il est
   * dans le SDK pour la même raison que tout le reste : une app n'a qu'une
   * porte vers le backend (règle d'or 1).
   */
  marketData: MarketDataClient;
  /**
   * LES COMPTES : creer un identifiant, redonner un mot de passe, retirer un
   * acces. Pas un repository non plus, et a ne pas confondre avec `members` :
   * `members` ecrit la FICHE (les droits, ce qui suffit a INVITER quelqu'un),
   * `comptes` fabrique de quoi ENTRER sans Google. Cf. acces-comptes.ts.
   */
  comptes: ComptesClient;
  contacts: ContactsRepository;
  assets: AssetsRepository;
  loans: LoanRepository;
  warranties: WarrantyRepository;
  incidents: IncidentRepository;
  measurements: MeasurementRepository;
  forms: FormRepository;
  campaigns: CampaignRepository;
  accessGrants: AccessGrantRepository;
  policies: PolicyRepository;
  registerSessions: RegisterSessionRepository;
  relationships: RelationshipRepository;
  groups: GroupRepository;
  places: PlaceRepository;
  priceLists: PriceListRepository;
  stocks: StockRepository;
  stockMovements: StockMovementRepository;
  availabilities: AvailabilityRepository;
  shifts: ShiftRepository;
  absences: AbsenceRepository;
  attendances: AttendanceRepository;
  subscriptions: SubscriptionRepository;
  agreements: AgreementRepository;
  donations: DonationRepository;
  expenses: ExpenseRepository;
  shipments: ShipmentRepository;
  custodies: CustodyRepository;
  tickets: TicketRepository;
  inspections: InspectionRepository;
  messages: MessageRepository;
  /**
   * LES CONVERSATIONS : un sujet avec Lia, un canal, un message direct, le
   * chatter d'une fiche. Premiere famille PORTEUSE d'un acces par fiche :
   * `acces` equipe, prive (ses participants) ou ancre (une fiche). Les messages
   * d'une conversation vivent dans `messages`, avec `conversationId`.
   * Cf. conversations.ts et `docs/socle/acces-par-fiche.md`.
   */
  conversations: ConversationRepository;
  reviews: ReviewRepository;
  consents: ConsentRepository;
  vouchers: VoucherRepository;
  credits: CreditRepository;
  compositions: CompositionRepository;
  lots: LotRepository;
  routes: RouteRepository;
  workers: WorkerRepository;
  products: ProductRepository;
  resources: ResourceRepository;
  bookings: BookingRepository;
  events: EventRepository;
  registrations: RegistrationRepository;
  orders: OrderRepository;
  invoices: InvoiceRepository;
  payments: PaymentRepository;
  workOrders: WorkOrderRepository;
  tasks: TaskRepository;
  todos: TodoRepository;
  timesheets: TimesheetRepository;
  documents: DocumentRepository;
  /**
   * LES CONTENUS des surfaces Canvas (un dessin, une carte mentale) : une
   * fiche sans charge, et une charge en morceaux que les rules gardent
   * coherents. Cf. contents.ts et `docs/DONNEES-SDK.md`, section « Les contenus ».
   */
  contents: ContentRepository;
  /** Binaires lies a une fiche, metadonnees Firestore et binaire Storage. */
  files: FilesRepository;
  projects: ProjectRepository;
  listings: ListingRepository;
  opportunities: OpportunityRepository;
  careRecords: CareRecordRepository;
  credentials: CredentialRepository;
  templates: TemplateRepository;
  /**
   * LES PLANS RECURRENTS : « cree cette fiche, a ce rythme ». Le client ecrit
   * et lit le plan ; les fiches sont creees par le passage quotidien, cote
   * serveur, et `lancer` en demande un tout de suite. Cf. recurrences.ts.
   */
  recurrences: RecurrenceRepository;
  /**
   * LES AUTOMATISATIONS (app Flux) : lectures en temps reel de `flux`,
   * `fluxExecutions` et `fluxVariables`, et la callable
   * `fluxApi` pour tout geste. Le client n'ecrit jamais : le serveur valide
   * par le schema et les droits. Pas une famille. Cf. flux.ts.
   */
  flux: FluxClient;
  /**
   * LES CONNEXIONS du socle : l'acces a un service exterieur (API par jeton,
   * demain mail et agenda), servi a Flux, Lia et au MCP. Lecture en temps reel
   * de `connexions` (jamais le secret), la callable `connexionsApi` pour tout
   * geste, et le registre des fournisseurs. Pas une famille. Cf. connexions.ts.
   */
  connexions: ConnexionsClient;
  /**
   * L'annuaire des UTILISATEURS de l'org et de leurs droits. Famille a part a
   * deux titres : son identifiant est l'email (pas un id genere), et elle vit
   * dans une sous-collection (`orgs/{orgId}/members`). Cf. members.ts.
   */
  members: MemberRepository;
  /**
   * L'ANNUAIRE DES PERSONNES de l'org (`orgs/{orgId}/personnes`) : nom, e-mail
   * et uid de chaque personne, lisible par tout membre actif, pour nommer un
   * `personId` sans ouvrir les fiches membres. En LECTURE : il s'ecrit avec la
   * fiche, par `sdk.members`. Pas une famille. Cf. personnes.ts.
   */
  personnes: PersonneRepository;
  /**
   * LE BRANDING DE L'ENVIRONNEMENT : le nom, le logo et la couleur d'accent que
   * le client choisit depuis Parametres. Ce n'est PAS une famille (un seul
   * document par org, identifiant fixe, pas de champs standard, pas de vues) :
   * il n'a donc ni `list`, ni agregats, et `repositoryOf('branding')` le refuse
   * pour la meme raison que `members`. Cf. branding.ts.
   */
  branding: BrandingRepository;
  /**
   * LES APPS INSTANCIEES PAR L'ORG : la declaration entiere d'une app telle que
   * le client l'a personnalisee, un document par app
   * (`orgs/{orgId}/apps/{appId}`), interprete a la place du modele du bundle.
   * Pas une famille non plus (document de reglage a identifiant fixe), refuse
   * par `repositoryOf` et `famille` comme le branding. Cf. apps-instanciees.ts.
   */
  appsInstanciees: AppsInstancieesRepository;
  /**
   * LES APPS INSTALLEES DE L'ORG (`orgs/{orgId}/settings/apps`) : la liste qui
   * fait le dock, ecrite depuis Parametres. Document de reglage, pas une
   * famille. Cf. installation-apps.ts.
   */
  appsInstallees: AppsInstalleesRepository;
  /**
   * L'ENVIRONNEMENT DE L'ORG (`orgs/{orgId}/settings/environnement`) :
   * `production` ou `staging`, lisible avant la connexion pour la bande
   * STAGING. Cf. installation-apps.ts.
   */
  environnement: EnvironnementRepository;
  /**
   * L'ETAT DES NUMEROS AUTOMATIQUES (`orgs/{orgId}/sequences/{cle}`, un document
   * par champ `numero`) : `lire` et `reglerProchain`. Le serveur attribue les
   * numeros ; le client ne regle que le prochain. Pas une famille. Cf.
   * sequences.ts et `docs/socle/champs-types.md`, section 4.
   */
  sequences: SequencesRepository;
  iaSettings: IaSettingsRepository;
  /**
   * LE MOTEUR DE NOTIFICATIONS, cote client : reglages, regles, preferences de
   * la personne, journal, et la callable `notifier` (essai, test d'une regle,
   * message libre, passage). Le moteur lui-meme vit dans `functions/`
   * (docs/NOTIFICATIONS.md). Pas une famille : pas de repository generique.
   */
  notifications: NotificationsRepository;
  iaHistory: IaHistoryRepository;
  /** Lia par WhatsApp : connecter son numero, le canal des notifications de l'org (`whatsapp.ts`). */
  whatsapp: WhatsappClient;
  /**
   * LES VUES HTML D'UNE FICHE deposees par le MCP : lire son brouillon pour
   * l'apercu, le publier par la callable `publierVueHtml`. Le client n'ecrit
   * jamais. Pas une famille. Cf. vues-html.ts, `docs/socle/vues-html.md`.
   */
  vuesHtml: VuesHtmlClient;
  /**
   * LE JOURNAL D'AUDIT de l'environnement : `list` le lit (proprietaire seul,
   * rules), `noter` fait ecrire un evenement d'app par le serveur. Pas une
   * famille : aucun client n'y ecrit. Cf. journal.ts et `docs/JOURNAL.md`.
   */
  journal: JournalClient;
  /**
   * LES CONVERSATIONS AVEC LIA de la personne connectee, un document par sujet
   * sous son uid (`lia-conversations.ts`). Transversal : pas une famille de
   * porte, refuse par `repositoryOf` et `famille` comme l'historique IA.
   */
  liaConversations: LiaConversationsRepository;
  /**
   * LES NOTES (app Smart Notes) : lectures en temps reel de `notes`,
   * `notesTags` et `notesCollections`, la callable `notesApi` pour tout geste,
   * le contenu Yjs edite a plusieurs (brique `collab.ts`) et les pieces
   * jointes dans Storage. Pas une famille. Cf. notes.ts.
   */
  notes: NotesClient;
  /**
   * LE REPOSITORY D'UNE FAMILLE DESIGNEE PAR SON NOM, pour lire une famille
   * qu'on ne connait qu'a l'execution, et pour patcher une de ses fiches
   * (cf. `FamilyRepository` : quatre lectures et `update`).
   *
   * Le nom attendu est la CLEF de cette interface, au PLURIEL : `assets`,
   * `bookings`, `careRecords`. C'est le vocabulaire de `taki.fields.json`, de
   * `porte.jeu.family` et du `type` d'un modele de fiche : le meme mot partout.
   * Le singulier (`booking`) n'est pas une famille du SDK, c'est le nom du
   * catalogue du Studio, qui le traduit avant d'ecrire un fichier d'app.
   *
   * LEVE sur une famille inconnue, plutot que de rendre `undefined` : un
   * `sdk[famille]` absent ne casse qu'a la premiere lecture, loin de la faute,
   * et c'est exactement le degat qu'a coute le catalogue du Studio quand il
   * proposait 55 noms au singulier.
   */
  repositoryOf(family: string): FamilyRepository;

  /**
   * LE SIGNAL DES ECRITURES : chaque `create`, `update`, `archive`, `unarchive`
   * et `remove` REUSSI d'une famille, par n'importe quel chemin
   * (`sdk.<famille>`, `famille(nom)`, `createIn`, `repositoryOf`, modeles). Un
   * ecran qui montre un total s'y abonne pour le recompter quand une surface
   * d'app a ecrit sans lui. En memoire, pour cet onglet. Cf. ecritures.ts.
   */
  ecritures: SignalDesEcritures;
  /**
   * CREE UNE FICHE DANS UNE FAMILLE DESIGNEE PAR SON NOM. Le verbe de CREATION
   * a cote du `update` de `repositoryOf` : c'est par lui que l'hote remplit
   * le `CreationPort` du socle (`packages/ui/src/lib/creation.tsx`), donc que
   * la fiche vide de « + Ajouter » devient une fiche reelle, sans qu'aucune app
   * n'ecrive une ligne pour creer.
   *
   * `input` est la fiche telle que le brouillon l'a rangee : les champs de
   * racine, le sac `customFields`, plus ce que l'HOTE ajoute (`orgId`, `type`).
   * Le repository valide par Zod et pose les champs standard, comme toujours.
   *
   * LEVE sur une famille inconnue, AVANT toute ecriture (cf. `creerDansFamille`).
   */
  createIn(family: string, input: Record<string, unknown>): Promise<{ id: string }>;
  /**
   * LE REPOSITORY COMPLET D'UNE FAMILLE DESIGNEE PAR SON NOM (cf.
   * `FamilleRepository`). Meme vocabulaire que `repositoryOf` (le pluriel du
   * SDK), meme refus sur un nom inconnu, AVANT toute lecture.
   */
  famille(nom: string): FamilleRepository;
}

/**
 * Construit le SDK à partir de la config chargée au runtime :
 * initialise Firebase, ouvre Firestore et expose les repositories typés.
 *
 * Si la config porte un bloc `emulators`, Firestore est branché sur l'émulateur
 * local plutôt que sur le cloud (cf. `initFirebase` et `pnpm dev:local`).
 */
export function createTakiSdk(cfg: TakiConfig): TakiSdk {
  const { app, db, storage } = initFirebase(cfg);
  const actorId = () => getAuth(app).currentUser?.uid ?? 'system';
  // L'EMAIL de l'utilisateur connecte, en minuscules, ou null : c'est lui qui
  // designe sa fiche membre (`sdk.members.me()`), exactement comme les Security
  // Rules le font avec `request.auth.token.email.lower()`. Pas de repli sur une
  // valeur par defaut ici : sans email il n'y a pas de fiche, et c'est un etat
  // normal (compte provisionne par script), pas une erreur a masquer.
  const actorEmail = () => getAuth(app).currentUser?.email?.toLowerCase() ?? null;
  // Le `personId` de l'appelant, lu une fois sur sa fiche membre et garde : ce
  // que les familles a ACCES PAR FICHE (`contents`, `conversations`, `messages`)
  // posent comme proprietaire d'une fiche privee et comme filtre d'une liste
  // privee. Lu paresseusement : une liste d'equipe ne le demande jamais.
  const maPersonne = creerMaPersonne(actorEmail, () => sdk.members.me());

  // Le repository des MODELES doit pouvoir creer dans N'IMPORTE QUELLE famille
  // (un modele de projet cree un projet et ses taches). Il ne peut pas importer
  // les 55 repositories sans les embarquer tous dans chaque app : il recoit
  // donc une fermeture qui lit l'objet SDK une fois construit. D'ou la variable
  // assignee apres coup : la fermeture n'est appelee qu'a l'instanciation d'un
  // modele, longtemps apres la fin de cette fonction.
  let sdk: TakiSdk;

  /**
   * LA TABLE DES FAMILLES, C'EST L'OBJET SDK LUI-MEME, et il n'est lu qu'ICI.
   * Ecrire une liste des 55 noms a cote de celle qui suit, c'est se donner deux
   * listes a tenir a jour, donc une qui sera fausse. Rend l'entree brute : ce
   * sont ses appelants qui disent ce qu'ils exigent d'elle.
   */
  const familyEntry = (family: string): Record<string, unknown> | null => {
    const candidat = (sdk as unknown as Record<string, unknown>)[family];
    if (candidat == null || typeof candidat !== 'object') return null;
    return candidat as Record<string, unknown>;
  };

  const resolveRepository = (family: string): TemplateTargetRepository | null => {
    const entree = familyEntry(family);
    // La propriete existe ET sait creer : `sdk.app` et `sdk.db` existent aussi,
    // et une famille inventee doit etre refusee, pas plantee a l'appel.
    if (entree == null || typeof entree['create'] !== 'function') return null;
    return entree as unknown as TemplateTargetRepository;
  };

  /**
   * Les verbes que `repositoryOf` PROMET. La verification porte sur ce qui sera
   * appele, pas sur la seule existence de la propriete : `sdk.app` et `sdk.db`
   * sont des objets eux aussi, et `sdk.members` en est un qui ne sait ni
   * compter ni agreger.
   *
   * `update` EN FAIT PARTIE DEPUIS QU'IL EST PROMIS : une famille qui ne
   * l'aurait pas casserait sur « is not a function » au moment du clic, loin de
   * sa cause. C'est la meme garde que `creerDansFamille` pose sur `create`,
   * refuser avant d'ecrire.
   */
  const VERBES_PROMIS = ['list', 'count', 'sum', 'average', 'update'] as const;

  const repositoryOf = (family: string): FamilyRepository => {
    const entree = familyEntry(family);
    // Meme vocabulaire que `creerDansFamille` et `famille`, ecrit une seule
    // fois dans `@taki/data-model` (`messageFamilleInconnue`).
    if (entree == null) throw new Error(messageFamilleInconnue(family));
    const manquants = VERBES_PROMIS.filter((verbe) => typeof entree[verbe] !== 'function');
    if (manquants.length > 0) {
      throw new Error(
        `La famille "${family}" existe dans le SDK mais ne porte pas ${manquants.join(', ')} : ` +
          "elle n'a pas la surface commune aux familles. C'est le cas de " +
          "l'annuaire des membres, qui vit dans une sous-collection (`orgs/{orgId}/members`), " +
          'sans cadrage `orgId` ni agrégats : passez par `sdk.members` (ou `sdk.personnes` ' +
          "pour l'annuaire des personnes).",
      );
    }
    return entree as unknown as FamilyRepository;
  };

  // Une seule instance : `sdk.flux.observerConnexions` lit par elle.
  const connexions = createConnexionsClient(db, actorId, cfg.orgId, app, cfg.emulators?.functions);
  let fuseauRegle: string | null = null;
  const signalDesEcritures = creerSignalDesEcritures();
  sdk = {
    get timezone() {
      return fuseauRegle ?? cfg.timezone ?? FUSEAU_PAR_DEFAUT;
    },
    app,
    db,
    marketData: createMarketDataClient(app, db, cfg.orgId, cfg.emulators?.functions),
    comptes: createComptesClient(app, cfg.emulators?.functions),
    contacts: createContactsRepository(db, actorId, cfg.orgId),
    assets: createAssetsRepository(db, actorId, cfg.orgId),
    loans: createLoanRepository(db, actorId, cfg.orgId),
    warranties: createWarrantyRepository(db, actorId, cfg.orgId),
    incidents: createIncidentRepository(db, actorId, cfg.orgId),
    measurements: createMeasurementRepository(db, actorId, cfg.orgId),
    forms: createFormRepository(db, actorId, cfg.orgId),
    campaigns: createCampaignRepository(db, actorId, cfg.orgId),
    accessGrants: createAccessGrantRepository(db, actorId, cfg.orgId),
    policies: createPolicyRepository(db, actorId, cfg.orgId),
    registerSessions: createRegisterSessionRepository(db, actorId, cfg.orgId),
    relationships: createRelationshipRepository(db, actorId, cfg.orgId),
    groups: createGroupRepository(db, actorId, cfg.orgId),
    places: createPlaceRepository(db, actorId, cfg.orgId),
    priceLists: createPriceListRepository(db, actorId, cfg.orgId),
    stocks: createStockRepository(db, actorId, cfg.orgId),
    stockMovements: createStockMovementRepository(db, actorId, cfg.orgId),
    availabilities: createAvailabilityRepository(db, actorId, cfg.orgId),
    shifts: createShiftRepository(db, actorId, cfg.orgId),
    absences: createAbsenceRepository(db, actorId, cfg.orgId),
    attendances: createAttendanceRepository(db, actorId, cfg.orgId),
    subscriptions: createSubscriptionRepository(db, actorId, cfg.orgId),
    agreements: createAgreementRepository(db, actorId, cfg.orgId),
    donations: createDonationRepository(db, actorId, cfg.orgId),
    expenses: createExpenseRepository(db, actorId, cfg.orgId),
    shipments: createShipmentRepository(db, actorId, cfg.orgId),
    custodies: createCustodyRepository(db, actorId, cfg.orgId),
    tickets: createTicketRepository(db, actorId, cfg.orgId),
    inspections: createInspectionRepository(db, actorId, cfg.orgId),
    // Un envoi marque la conversation lue : `sdk.conversations.lecture`, resolu a l'appel (meme patron que `sdk.members.me()`).
    messages: createMessageRepository(db, actorId, cfg.orgId, maPersonne, (conversationId) => sdk.conversations.lecture.marquerLu(conversationId),
      (conversationId) => sdk.conversations.verifierEcriture(conversationId)),
    conversations: createConversationRepository(db, actorId, cfg.orgId, maPersonne, async () => (await sdk.members.me())?.orgRole ?? null,
      createLecteurEtatDuChatter(app, cfg.emulators?.functions)),
    reviews: createReviewRepository(db, actorId, cfg.orgId),
    consents: createConsentRepository(db, actorId, cfg.orgId),
    vouchers: createVoucherRepository(db, actorId, cfg.orgId),
    credits: createCreditRepository(db, actorId, cfg.orgId),
    compositions: createCompositionRepository(db, actorId, cfg.orgId),
    lots: createLotRepository(db, actorId, cfg.orgId),
    routes: createRouteRepository(db, actorId, cfg.orgId),
    workers: createWorkerRepository(db, actorId, cfg.orgId),
    products: createProductRepository(db, actorId, cfg.orgId),
    resources: createResourceRepository(db, actorId, cfg.orgId),
    bookings: createBookingRepository(db, actorId, cfg.orgId),
    events: createEventRepository(db, actorId, cfg.orgId),
    registrations: createRegistrationRepository(db, actorId, cfg.orgId),
    orders: createOrderRepository(db, actorId, cfg.orgId),
    invoices: createInvoiceRepository(db, actorId, cfg.orgId),
    payments: createPaymentRepository(db, actorId, cfg.orgId),
    workOrders: createWorkOrderRepository(db, actorId, cfg.orgId),
    tasks: createTaskRepository(db, actorId, cfg.orgId),
    todos: createTodoRepository(db, actorId, cfg.orgId),
    timesheets: createTimesheetRepository(db, actorId, cfg.orgId),
    documents: createDocumentRepository(db, actorId, cfg.orgId),
    contents: createContentRepository(db, actorId, cfg.orgId, maPersonne),
    files: createFilesRepository(db, storage, actorId, cfg.orgId,
      cfg.emulators == null || cfg.emulators.storage != null,
      app, cfg.emulators?.functions, cfg.emulators == null || cfg.emulators.functions != null),
    projects: createProjectRepository(db, actorId, cfg.orgId),
    listings: createListingRepository(db, actorId, cfg.orgId),
    opportunities: createOpportunityRepository(db, actorId, cfg.orgId),
    careRecords: createCareRecordRepository(db, actorId, cfg.orgId),
    credentials: createCredentialRepository(db, actorId, cfg.orgId),
    templates: createTemplateRepository(db, actorId, cfg.orgId, resolveRepository, createTemplatePlanValidator(app, cfg.orgId, cfg.emulators?.functions, cfg.emulators == null || cfg.emulators.functions != null)),
    // Le repository des plans lit celui des modeles A L'APPEL (un plan lit son
    // modele, « Créer un plan récurrent » en ecrit un) : une fermeture, comme
    // `resolveRepository`, puisque `sdk.templates` n'existe qu'une fois l'objet
    // construit.
    recurrences: createRecurrenceRepository(db, actorId, cfg.orgId, app, cfg.emulators?.functions, () => sdk.templates),
    flux: createFluxClient(db, actorId, cfg.orgId, app, cfg.emulators?.functions, connexions),
    connexions,
    members: createMemberRepository(db, actorId, actorEmail, cfg.orgId),
    personnes: createPersonneRepository(db, cfg.orgId),
    branding: createBrandingRepository(db, actorId, cfg.orgId),
    appsInstanciees: createAppsInstancieesRepository(db, actorId, cfg.orgId, app, cfg.emulators?.functions),
    appsInstallees: createAppsInstalleesRepository(db, actorId, cfg.orgId),
    environnement: createEnvironnementRepository(db, actorId, cfg.orgId, (fuseau) => {
      fuseauRegle = fuseau;
    }),
    sequences: createSequencesRepository(db, actorId, cfg.orgId),
    iaSettings: createIaSettingsRepository(db, actorId, cfg.orgId),
    iaHistory: createIaHistoryRepository(db),
    whatsapp: createWhatsappClient(app, db, cfg.orgId, actorId, cfg.emulators?.functions),
    vuesHtml: createVuesHtmlClient(db, cfg.orgId, app, cfg.emulators?.functions),
    journal: createJournalClient(db, app, cfg.orgId, cfg.emulators?.functions),
    notifications: createNotificationsRepository(db, actorId, actorEmail, cfg.orgId, app, cfg.emulators?.functions),
    liaConversations: createLiaConversationsRepository(db, actorId, cfg.orgId, maPersonne),
    notes: createNotesClient(db, storage, actorId, cfg.orgId, app, cfg.emulators?.functions),
    repositoryOf,
    createIn: (family, input) => creerDansFamille(resolveRepository, family, input),
    famille: (nom) => creerFamilleRepository(familyEntry, nom),
    ecritures: signalDesEcritures,
  };
  // AVANT que quiconque lise un repository : les cinq verbes de chaque FAMILLE
  // (la liste du data-model, pas une de plus) sont enveloppes sur place. Les
  // depots transversaux (members, branding, apps...) ne sont pas des familles et
  // n'y sont pas. Une entree de la liste qui ne serait pas un objet est ignoree.
  for (const famille of FAMILLES) {
    const repository = familyEntry(famille);
    if (repository != null) envelopperLesEcritures(famille, repository, signalDesEcritures);
  }
  return sdk;
}
